"use client";

import { useCallback, useState } from "react";
import { formatCentsAsCurrency } from "@/lib/forms/format-price";

/** What the caller's `submitForm` must resolve to for the payment flow to proceed. */
interface SubmitResult {
  responseId: number | null;
  email: string | null;
  cleanedFormData: Record<string, unknown>;
}

interface UseFormPaymentResult {
  hasPayment: boolean;
  showCheckout: boolean;
  paymentClientSecret: string | null;
  paymentResponseId: number | null;
  paymentEmail: string | null;
  resolvedAmountDisplay: string;
  /** Captured at submit time so the caller can send a deferred confirmation email once payment is confirmed, without recomputing it. */
  paymentCleanedFormData: Record<string, unknown> | null;
  /**
   * Runs the submit-then-pay flow: submits the form (via `submitForm`, owned by the
   * caller since it depends on form-field state this hook doesn't have), then creates
   * a PaymentIntent for the resulting response. If nothing needs charging (a free
   * tier resolved), calls `onFreeCompletion` directly instead of opening the checkout
   * step — there's no payment step left to show.
   *
   * @throws When the response/email can't be resolved, or PaymentIntent creation fails.
   */
  preparePayment: (
    submitForm: () => Promise<SubmitResult>,
    onFreeCompletion: (cleanedFormData: Record<string, unknown>, responseId: number) => void | Promise<void>
  ) => Promise<void>;
  closeCheckout: () => void;
}

/**
 * Owns the submit-then-pay orchestration for FormModal: creating a PaymentIntent for
 * a just-submitted response and tracking the state CheckoutStep needs to render.
 *
 * @param paymentEnabled - The form's `payment_enabled` setting. This alone gates
 *   `hasPayment` — the actual amount (respondent-type price, legacy flat price, or
 *   free) is resolved server-side by create-payment-intent, which already returns
 *   `noPaymentIntent: true` when there's nothing to charge.
 */
export function useFormPayment(paymentEnabled: boolean): UseFormPaymentResult {
  const hasPayment = paymentEnabled;

  // States
  const [showCheckout, setShowCheckout] = useState(false);
  // Populated by the submit-then-pay flow: the response is saved (status: submitted,
  // payment_status: pending) BEFORE the card form ever renders, since PaymentElement
  // needs a real clientSecret to mount at all.
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [paymentResponseId, setPaymentResponseId] = useState<number | null>(null);
  const [paymentEmail, setPaymentEmail] = useState<string | null>(null);
  // The real, resolved amount from create-payment-intent — may differ from the legacy
  // flat paymentAmountDisplay string once a respondent-type price applies.
  const [resolvedAmountDisplay, setResolvedAmountDisplay] = useState("");
  const [paymentCleanedFormData, setPaymentCleanedFormData] = useState<Record<string, unknown> | null>(null);

  // Handlers
  const preparePayment = useCallback(
    async (
      submitForm: () => Promise<SubmitResult>,
      onFreeCompletion: (cleanedFormData: Record<string, unknown>, responseId: number) => void | Promise<void>
    ) => {
      const { responseId, email, cleanedFormData } = await submitForm();
      if (!responseId || !email) {
        throw new Error("Could not resolve a submitted email for payment");
      }

      const piResponse = await fetch("/api/forms/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ responseId, email }),
      });
      const piData = await piResponse.json();

      if (piData.noPaymentIntent) {
        // Nothing to charge (free tier resolved) — there's no payment step left to
        // defer to, so the caller finalizes the submission now.
        await onFreeCompletion(cleanedFormData, responseId);
        return;
      }
      if (!piResponse.ok || !piData.clientSecret) {
        throw new Error(piData.error || "Failed to prepare payment");
      }

      setPaymentResponseId(responseId);
      setPaymentEmail(email);
      setPaymentClientSecret(piData.clientSecret);
      setPaymentCleanedFormData(cleanedFormData);
      setResolvedAmountDisplay(formatCentsAsCurrency(piData.amountMinorUnits, piData.currency));
      setShowCheckout(true);
    },
    []
  );

  const closeCheckout = useCallback(() => setShowCheckout(false), []);

  return {
    hasPayment,
    showCheckout,
    paymentClientSecret,
    paymentResponseId,
    paymentEmail,
    resolvedAmountDisplay,
    paymentCleanedFormData,
    preparePayment,
    closeCheckout,
  };
}
